<template>
  <div class="avatar-builder-category" :id="category.key" :ref="category.key">
    <div
      v-if="
        category.key === OUTFIT_CATEGORY_KEY ||
          DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(category.key)
      "
    >
      <div
        v-if="visible"
        class="fashion-traits-container"
        :id="getCategoryIdentifier"
        :class="getVerticalScrollClass"
        @scroll="onScrollToBottom"
      >
        <div v-if="shouldShowBanner" class="snapchat-upsell-banner-container">
          <SnapchatUpsellBanner :categoryKey="category.key" />
        </div>
        <!-- Warning text for when a user is in a mix and match category and is currently wearing a regular/legacy outfit -->
        <span
          class="disclaimer"
          v-if="shouldShowRemoveCurrentOutfitDisclaimer"
          v-localize="getCurrentCategoryDisclaimer"
        />
        <div
          v-if="category.key === OUTFIT_CATEGORY_KEY"
          :class="{ 'additional-padding': showGradient }"
        >
          <AvatarBuilderFashionCategory
            @reload-category-grid="forceRender"
            v-for="(option, index) in filteredOptions"
            :key="`${option.name}.${counter}`"
            :category-key="category.key"
            :brand-data="option"
            :brand-index="index"
            :should-force-load-brand="shouldForceLoadBrand(option.id)"
            :tab-navigation-enabled="tabNavigationEnabled"
          />
        </div>
        <div
          v-if="categoryDetails && DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(category.key)"
        >
          <AvatarBuilderMixAndMatchCategory
            :category-style="categoryOptionsStyle"
            v-for="(option, index) in filteredOptions"
            :key="`${category.key}${option.name}.${counter}`"
            :category-key="category.key"
            :brand-index="index"
            :brand-data="option"
            :brand-num="filteredOptions.length"
            :category-details="categoryDetails"
            :should-force-load-brand="shouldForceLoadBrand(option.id)"
            :tab-navigation-enabled="tabNavigationEnabled"
          />
        </div>
        <!-- TODO(kc): Remove this once more socks are added -->
        <div v-if="category.key === MixAndMatchCategoryEnum.SOCK" class="fill-blank-space"></div>
      </div>
    </div>

    <div v-else-if="category.key === 'save'" class="save-button-container">
      <SaveCategory v-if="visible" :tab-navigation-enabled="tabNavigationEnabled" />
    </div>

    <div v-else-if="visible">
      <div
        v-if="shouldShowCategory && categoryDetails"
        :style="traitsContainerStyle"
        class="traits-container"
        :id="getCategoryIdentifier"
        :class="getVerticalScrollClass"
        @scroll="onScrollToBottom"
      >
        <!-- Snapchat Upsell Banner -->
        <SnapchatUpsellBanner v-if="shouldShowBanner" :categoryKey="category.key" />
        <span
          class="disclaimer"
          v-if="shouldShowOverrideHeadwearWarning"
          v-localize="HEADWEAR_OVERRIDE_WARNING_TEXT_KEY"
        />
        <AvatarBuilderTraitPreview
          :style="categoryOptionsStyle"
          @reload-category-grid="forceRender"
          v-for="(option, index) in filteredOptions"
          :key="option.value === undefined ? undefined : `${option.value}.${counter}`"
          :option-index="index"
          :category-key="category.key"
          :category-details="categoryDetails"
          :option="option"
          :tab-navigation-enabled="tabNavigationEnabled"
        />
      </div>
      <div v-else class="category-text">
        <span
          v-if="this.category.key === 'hair_tone'"
          v-localize="HAIR_TONE_CATEGORY_WARNING_TEXT_KEY"
        />
        <span
          v-if="this.category.key === 'hair_treatment_tone'"
          v-localize="HAIR_TREATMENT_TONE_WARNING_TEXT_KEY"
        />
        <span v-if="this.category.key === 'beard_tone'" v-localize="BEARD_TONE_WARNING_TEXT_KEY" />
        <span
          v-if="this.category.key === 'my_closet' && !shouldShowSpinner"
          v-localize="getClosetMessage"
        />
        <Spinner v-if="shouldShowSpinner"></Spinner>
      </div>
    </div>
  </div>
</template>

<script>
import AvatarBuilderTraitPreview from '@/components/AvatarBuilderUI/AvatarBuilderTraitPreview';
import AvatarBuilderFashionCategory from '@/components/AvatarBuilderUI/AvatarBuilderFashionCategory';
import AvatarBuilderMixAndMatchCategory from '@/components/AvatarBuilderUI/AvatarBuilderMixAndMatchCategory';
import SaveCategory from '@/components/AvatarBuilderUI/SaveCategory';
import { TRAIT_PREVIEW_NUM_ROWS_ALWAYS_SHOW, OUTFIT_CATEGORY_KEY } from '@/constants';
import CategoryAssets from '@/services/CategoryAssets';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import DynamicOutfitsOptions from '@/services/DynamicOutfitsOptions';
import { ScrollStateEnum } from '@/enum/scroll_state';
import { ClosetStateEnum } from '@/enum/closet_state';
import { RenderMode } from '@/enum/render_mode';
import { LocalizationDirective } from '@/directives/LocalizationDirective';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import { MixAndMatchCategoryEnum } from '@/enum/mix_and_match';
import { SixthBodyEnum } from '@/enum/sixth_body';
import ReceiveClientMessage from '@/services/ReceiveClientMessage';
import FeatureGating from '@/services/FeatureGating';
import Spinner from '@/components/ReusableComponents/Spinner';
import SnapchatUpsellBanner from '@/components/AvatarBuilderUI/SnapchatUpsellBanner';

export default {
  name: 'AvatarBuilderCategory',
  components: {
    AvatarBuilderTraitPreview,
    AvatarBuilderFashionCategory,
    SaveCategory,
    Spinner,
    AvatarBuilderMixAndMatchCategory,
    SnapchatUpsellBanner,
  },
  props: [
    'category', //: Object,
    'categoryIndex', //: Number
    'showGradient', //: Boolean
  ],
  created: function() {
    this.HAIR_TONE_CATEGORY_WARNING_TEXT_KEY = 'NoHairToneExplanation';
    this.HAIR_TREATMENT_TONE_WARNING_TEXT_KEY = 'NoHairTreatmentToneExplanation';
    this.BEARD_TONE_WARNING_TEXT_KEY = 'NoBeardToneExplanation';
    this.HEADWEAR_OVERRIDE_WARNING_TEXT_KEY = 'HeadwearOverrideWarning';
    this.REMOVE_CURRENT_OUTFIT_TOP_WARNING_TEXT_KEY = 'RemoveCurrentOutfitFromChoosingTopWarning';
    this.REMOVE_CURRENT_OUTFIT_BOTTOM_WARNING_TEXT_KEY =
      'RemoveCurrentOutfitFromChoosingBottomWarning';
    this.REMOVE_CURRENT_OUTFIT_FOOTWEAR_WARNING_TEXT_KEY =
      'RemoveCurrentOutfitFromChoosingFootwearWarning';
    this.REMOVE_CURRENT_OUTFIT_SOCK_WARNING_TEXT_KEY = 'RemoveCurrentOutfitFromChoosingSockWarning';
    this.REMOVE_CURRENT_OUTFIT_OUTERWEAR_WARNING_TEXT_KEY =
      'RemoveCurrentOutfitFromChoosingOuterwearWarning';
    this.REMOVE_CURRENT_OUTFIT_DRESS_WARNING_TEXT_KEY =
      'RemoveCurrentOutfitFromChoosingDressWarning';
    this.EMPTY_CLOSET_WARNING_TEXT_KEY = 'EmptyClosetExplanation';
    this.CLOSET_ERROR_MESSAGE_TEXT_KEY = 'ClosetErrorMessage';
    this.CLOSET_DO_NOT_SUPPORT_TEXT_KEY = 'ClosetIsNotSupportedMessage';
    this.MixAndMatchCategoryEnum = MixAndMatchCategoryEnum;
    this.DetailedOutfitHelper = DetailedOutfitHelper;
    this.OUTFIT_CATEGORY_KEY = OUTFIT_CATEGORY_KEY;
  },
  data: function() {
    return {
      categoryDetails: null,
      counter: 0,
      shouldForceLoadNextSection:
        this.categoryIndex === this.$store.getters.selectedCategoryIndex &&
        this.$store.getters.initialSectionId != null,
      clientWidth: 0,
      clientHeight: 0,
      atScrollEnd: false,
    };
  },
  computed: {
    categoryOptionsStyle: function() {
      // Set the row height to be equal to the column width.
      // This enforces all options to be rendered in square cells.
      // If the current category is my closet, then set the height ot 150px (same as fashion)

      // TODO(kc): remove this once sixth body is 100% roll out
      const height =
        this.category.key === 'my_closet'
          ? 170
          : this.clientWidth / this.categoryDetails.column_count;

      // 3% height margin
      const marginBottom = this.clientHeight * 0.03;

      // Special styling for mix and match categories
      if (DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.category.key)) {
        return {
          width: `30.25%`,
          margin: `0 0 2% 2%`,
        };
      }

      // Default styling for all other categories
      // each individual column takes equal share of width
      const widthPercent = 100 / this.categoryDetails.column_count - 1;
      const marginHorizontal = 0.5;

      if (this.category.key === 'body') {
        return {
          width: `${widthPercent}%`,
          margin: `0 ${marginHorizontal}% ${marginBottom}px`,
        };
      }

      if (this.category.key === 'my_closet') {
        const closetWidthPercent = 100 / this.categoryDetails.column_count - 2;
        return {
          width: `${closetWidthPercent}%`,
          height: `${height}px`,
          margin: `0 1% ${marginBottom}px`,
        };
      }

      if (
        ['lipstick_tone', 'blush_tone', 'eyeshadow_tone', 'pupil_tone'].includes(this.category.key)
      ) {
        return {
          width: `${widthPercent}%`,
          height: `${height}px`,
          margin: `0 ${marginHorizontal}% 0px`,
        };
      }

      if (this.category.key === 'skin_tone') {
        return {
          width: `${widthPercent}%`,
          height: `${height * 0.8}px`,
          margin: `2.5px ${marginHorizontal}% 0px`,
        };
      }

      return {
        width: `${widthPercent}%`,
        height: `${height}px`,
        margin: `0 ${marginHorizontal}% ${marginBottom}px`,
      };
    },
    shouldShowBanner: function() {
      return (
        (FeatureGating.isSnapchatUpsellBannerEnabled() || FeatureGating.isAppDeprecationBannerEnabled()) &&
        (this.category.key === 'outfit' || this.category.key === 'hair')
      );
    },
    traitsContainerStyle: function() {
      if (DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.category.key)) {
        return {
          maxHeight: '100%',
        };
      }

      if (
        FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(this.category.key) ||
        this.category.key === 'face_proportion'
      ) {
        if (this.showGradient) {
          return {
            padding: '2% 1.5% 5% 1.5%',
            maxHeight: '100%',
          };
        }
        return {
          padding: '2% 1.5% 0 1.5%',
          maxHeight: '100%',
        };
      }
      if (this.showGradient) {
        return {
          padding: '2% 1.5% 5% 1.5%',
          maxHeight: '100%',
        };
      }
      return {
        padding: '2% 1.5% 0 1.5%',
        height: '100%',
      };
    },
    shouldShowCategory: function() {
      const categoryMetadata = this.$store.getters.categoryMetadata;

      // Check if hair_tone category should be shown for the selected hair
      if (this.category.key === 'hair_tone') {
        return !categoryMetadata.colourless_hair.includes(this.$store.getters.traits['hair']);
      }

      // Check if hair_treatment_tone category should be shown for the selected hair
      if (this.category.key === 'hair_treatment_tone') {
        if (!this.$store.getters.traits['hair']) {
          return false;
        }
        return categoryMetadata.hair_untreated_to_treated.hair_style_mappings[
          this.$store.getters.traits['hair']
        ];
      }

      // Check if beard_tone category should be shown for the selected beard
      if (this.category.key === 'beard_tone') {
        if (!this.$store.getters.traits['beard']) {
          return false;
        }
        return !categoryMetadata.colourless_beards.includes(this.$store.getters.traits['beard']);
      }

      // Check if my_closet category is empty
      if (this.category.key === 'my_closet' && !this.category.trait_category_options.length) {
        return false;
      }

      return true;
    },
    isAtScrollEnd: function() {
      return this.showGradient && this.atScrollEnd;
    },
    isNotAtScrollEnd: function() {
      return this.showGradient && !this.atScrollEnd;
    },
    renderMode: function() {
      if (this.categoryIndex === this.$store.getters.selectedCategoryIndex) {
        return RenderMode.REGULAR;
      } else if (
        this.categoryIndex === this.$store.getters.selectedCategoryIndex - 1 ||
        this.categoryIndex === this.$store.getters.selectedCategoryIndex + 1
      ) {
        return RenderMode.PRELOAD;
      } else {
        return RenderMode.HIDDEN;
      }
    },
    visible: function() {
      return (
        this.categoryIndex === this.$store.getters.selectedCategoryIndex ||
        this.categoryIndex === this.$store.getters.selectedCategoryIndex - 1 ||
        this.categoryIndex === this.$store.getters.selectedCategoryIndex + 1
      );
    },
    tabNavigationEnabled: function() {
      return this.categoryIndex === this.$store.getters.selectedCategoryIndex;
    },
    getVerticalScrollClass: function() {
      // If the user is scrolling horizontally (between the categories),
      // they should not be able to scroll vertically within a category
      return this.$store.getters.scrollState !== ScrollStateEnum.HORIZONTAL
        ? 'scrollable'
        : 'not-scrollable';
    },
    getCategoryIdentifier: function() {
      // Set id of div for automation testing to make it easier for interaction
      if (this.categoryIndex === this.$store.getters.selectedCategoryIndex) {
        return 'current-category';
      }
      if (this.categoryIndex === this.$store.getters.selectedCategoryIndex - 1) {
        return 'previous-category';
      }
      if (this.categoryIndex === this.$store.getters.selectedCategoryIndex + 1) {
        return 'next-category';
      }
      return '';
    },
    shouldShowRemoveCurrentOutfitDisclaimer: function() {
      if (
        DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.category.key) &&
        this.$store.getters.traits.clothing_type !== OutfitTypeEnum.SPLIT
      ) {
        return true;
      }
      return false;
    },
    shouldShowOverrideHeadwearWarning: function() {
      return (
        this.category.key === 'hat' &&
        this.$store.getters.traits.clothing_type === OutfitTypeEnum.ORIGINAL &&
        DynamicOutfitsOptions.dynamicOutfitsOptions[this.$store.getters.traits.outfit]
      );
    },
    getCurrentCategoryDisclaimer: function() {
      if (this.category.key === MixAndMatchCategoryEnum.TOP) {
        return this.REMOVE_CURRENT_OUTFIT_TOP_WARNING_TEXT_KEY;
      } else if (this.category.key === MixAndMatchCategoryEnum.BOTTOM) {
        return this.REMOVE_CURRENT_OUTFIT_BOTTOM_WARNING_TEXT_KEY;
      } else if (this.category.key === MixAndMatchCategoryEnum.FOOTWEAR) {
        return this.REMOVE_CURRENT_OUTFIT_FOOTWEAR_WARNING_TEXT_KEY;
      } else if (this.category.key === MixAndMatchCategoryEnum.SOCK) {
        return this.REMOVE_CURRENT_OUTFIT_SOCK_WARNING_TEXT_KEY;
      } else if (this.category.key === MixAndMatchCategoryEnum.DRESS) {
        return this.REMOVE_CURRENT_OUTFIT_DRESS_WARNING_TEXT_KEY;
      }
      // TODO(kcharoenphiphob): once more categories are added this has to be changed.
      return this.REMOVE_CURRENT_OUTFIT_OUTERWEAR_WARNING_TEXT_KEY;
    },
    shouldShowSpinner: function() {
      return (
        this.category.key === 'my_closet' &&
        this.$store.getters.closetState === ClosetStateEnum.LOADING
      );
    },
    getClosetMessage: function() {
      if (this.$store.getters.closetState === ClosetStateEnum.LOAD_FAILED) {
        return this.CLOSET_ERROR_MESSAGE_TEXT_KEY;
      } else if (this.$store.getters.closetState === ClosetStateEnum.DO_NOT_SUPPORT) {
        return this.CLOSET_DO_NOT_SUPPORT_TEXT_KEY;
      }
      return this.EMPTY_CLOSET_WARNING_TEXT_KEY;
    },
    // Filter out outfits for closets when user is wearing a sixth body
    filteredOptions: function() {
      if (this.category.key === OUTFIT_CATEGORY_KEY) {
        return this.getFilteredOutfits();
      } else if (DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.category.key)) {
        return this.getFilteredMixAndMatchOptions();
      } else {
        return this.getFilteredOptions();
      }
    },
  },
  mounted: function() {
    if (this.category.key === 'skin_tone') {
      this.categoryDetails = CategoryAssets.getCategoryDetails('skin_tone_swatch');
    } else {
      this.categoryDetails = CategoryAssets.getCategoryDetails(this.category.key);
    }
    ReceiveClientMessage.ClosetDataLoadedEventBus.$on('loaded', this.onClosetDataLoaded);
    // Scroll the initial category to the initial section.
    if (
      this.$store.getters.initialSectionId !== null &&
      this.categoryIndex === this.$store.getters.selectedCategoryIndex
    ) {
      // this.$nextTick ensures that components for brand sections have been mounted, before scrolling to the brand section.
      this.$nextTick(() => {
        this.scrollToSection(this.$store.getters.initialSectionId);
        this.$store.commit('setInitialSectionIdToDefault');
      });
    }
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  directives: {
    LocalizationDirective,
  },
  methods: {
    onResize() {
      this.clientWidth = this.$el.clientWidth;
      this.clientHeight = this.$el.clientHeight;
    },
    onClosetDataLoaded: function(data, error) {
      if (error) {
        this.$store.commit('setClosetState', { closetState: ClosetStateEnum.LOAD_FAILED });
      } else {
        this.$store.commit('setClosetItems', { closetItems: JSON.parse(data) });
      }
    },
    onScrollToBottom({ target: { scrollTop, clientHeight, scrollHeight } }) {
      this.$emit('scroll', scrollTop + clientHeight >= scrollHeight);
    },
    forceRender() {
      this.counter += 1;
    },
    scrollToSection(sectionId) {
      if (
        this.category.key === OUTFIT_CATEGORY_KEY ||
        DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.category.key)
      ) {
        const categoryElement = this.$el.querySelector('#current-category');
        // Format of ids of section container divs is <categoryKey>-<sectionId>.
        const sectionElement = this.$el.querySelector(`#${this.category.key}-${sectionId}`);
        if (categoryElement && sectionElement) {
          categoryElement.scrollTop = sectionElement.offsetTop;
        }
      }
    },
    shouldForceLoadBrand(brandId) {
      // Before deeplinking to a certain brand, we need to make sure that all brand sections
      // above this brand are loaded.
      if (!this.shouldForceLoadNextSection) {
        return false;
      }
      if (this.$store.getters.initialSectionId === brandId) {
        // After reaching the specified initial brand section, we can lazy-load the remaining
        // sections.
        this.shouldForceLoadNextSection = false;
      }
      return true;
    },
    getFilteredOutfits: function() {
      if (
        this.$store.getters.traits.body === SixthBodyEnum.MALE ||
        this.$store.getters.traits.body === SixthBodyEnum.FEMALE
      ) {
        const filteredOutfits = this.$store.getters.sixthBodyLegacyOutfits;

        return filteredOutfits;
      }
      return this.category.options;
    },
    getFilteredMixAndMatchOptions: function() {
      const options = this.category.options;
      let index = 0;
      options.forEach((option) => {
        if (option.garments && option.garments.length) {
          option.garments.forEach((garment) => {
            garment.index = index;
            index++;
          });
        }
      });
      return options;
    },
    getFilteredOptions: function() {
      if (
        (this.$store.getters.traits.body === SixthBodyEnum.MALE ||
          this.$store.getters.traits.body === SixthBodyEnum.FEMALE) &&
        this.category.key === 'my_closet'
      ) {
        return this.$store.getters.closetItemsSixthBody;
      }

      if (this.renderMode === RenderMode.HIDDEN) {
        return [];
      } else if (this.renderMode === RenderMode.PRELOAD) {
        // Truncate options if it is PRELOAD tab
        const numOptionsToAlwaysShow =
          this.categoryDetails.column_count * TRAIT_PREVIEW_NUM_ROWS_ALWAYS_SHOW;
        return this.category.trait_category_options.slice(0, numOptionsToAlwaysShow);
      }
      return this.category.trait_category_options;
    },
  },
};
</script>

<style scoped>
.avatar-builder-category {
  position: relative;
}

.traits-container {
  /* Use all the available space in parent relative positioned */
  /* Height is set in traitsContainerStyle since we want different height for the mix and match category */
  width: 100%;
  position: absolute;
  right: 0;
  top: 0;

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  box-sizing: border-box;
}

.fashion-traits-container {
  /* Use all the available space in parent relative positioned */
  height: 100%;
  width: 100%;
  position: absolute;
  right: 0;
  top: 0;
}

.additional-padding {
  padding: 0 0 30px 0;
}

.category-text {
  position: absolute;
  height: 100%;
  width: 100%;
  /* Vertically and horizontally center the child elements */
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: pre-line;
}

.category-text span {
  /* Ensure text doesn't touch the edge of the container */
  max-width: 65%;
}

.save-button-container {
  position: absolute;
  /* Required for scrolling */
  height: 100%;
  width: 100%;
  /* Center the button vertically and horizontally */
  display: flex;
  justify-content: center;
  align-items: center;
}

::-webkit-scrollbar {
  display: none;
  width: 0px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}

.scrollable {
  overflow-y: scroll;
}

.not-scrollable {
  overflow-y: hidden;
}

.snapchat-upsell-banner-container {
  padding: 10px 10px 0 10px;
}

.disclaimer {
  font-size: 14px;
  font-weight: 450;
  color: #828c9a;
  width: 100%;
  margin-bottom: 15px;
  justify-content: center;
}

.fill-blank-space {
  width: 100%;
  height: 25vh;
}

@media (prefers-color-scheme: dark) {
  .avatar-builder-category {
    background-color: #121212;
  }
  .traits-container {
    background-color: #121212;
  }
  .fashion-traits-container {
    background-color: #121212;
  }
  .category-text {
    color: #797a80;
  }
  .disclaimer {
    color: #797a80;
  }
}
</style>
